import { DEFAULT_YEAR_FORMAT, TechCardStatuses, useAppSelector, UserRoles, useTechCard } from '@/shared';
import { FormPreformModel } from '@/widgets/tech-card/model/preform.types';
import { Table, Th, Tr, Td, Thead, Tbody, Text, Input, Flex, chakra } from '@chakra-ui-kraud/react';
import { FC, useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styles from './custom-operations.module.scss';
import { isOperationDone, isOperationDoneAndEmptyField } from '../lib/operations-utils';
import { OperationsPreformBlockProps } from '../model/operations.types';
import {
	PreformOperationResponse,
	useGetAllExecutorsQuery,
	useGetPreformTechMapByNumberQuery,
} from '@/shared/state/api/swagger';
import { selectExecutorsForOptions } from '@/entities/employee';
import dayjs from 'dayjs';
import { DatePicker, Select } from 'antd';
import classNames from 'classnames';
import { byStepId } from '@/widgets/tech-card/lib/sort-by-step-id';
import { useParams } from 'react-router-dom';
import { convertNumberToNumberStringWithComma } from '@/shared/core/utils/convert-string-to-number-string';
import defaultStyles from '../operations/operations.module.scss';
import { ModalOperation } from '../modal-operation';
import { usePreformOperationsActions } from '../operations/operations-hooks';

export const OperationsPreformBlock: FC<OperationsPreformBlockProps> = ({ isEditable }) => {
	const { control, setValue, getValues } = useFormContext<FormPreformModel>();
	const { isCreationMode } = useTechCard();
	const mapNumber = Number(useParams().number);
	const { data: preformMap } = useGetPreformTechMapByNumberQuery(
		{ preformTechMapNumber: mapNumber },
		{
			skip: !mapNumber || Number.isNaN(mapNumber),
		},
	);
	const { preform_operations: fields } = getValues();
	const userRole = useAppSelector((state) => state.auth.userProfile?.role);
	const { options: employees } = useGetAllExecutorsQuery(
		{ stringToSearch: '' },
		{
			selectFromResult: (result) => ({ ...result, options: selectExecutorsForOptions(result?.data) }),
		},
	);

	const {
		renderCancelButton,
		renderRenewOperationButton,
		cancelOperationInfo,
		setCancelOperationInfo,
		renewalOperation,
		setRenewalOperation,
	} = usePreformOperationsActions();

	const canRenew = userRole !== UserRoles.storekeeper && preformMap?.status === TechCardStatuses.progress;

	// отмена операции
	const handleCancelOperation = useCallback(() => {
		const index = fields?.findIndex((operation) => operation.id === cancelOperationInfo.operation?.id) ?? -1;

		// следующая неотмененная операция
		const nextNotCanceledOperation = fields?.find(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);
		const nextNotCanceledOperationIndex = fields?.findIndex(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);

		if (Number(index) > -1) {
			setValue(`preform_operations.${index}.is_canceled`, true);
			setValue(`preform_operations.${index}.fio`, cancelOperationInfo.closingEmployeeFio);
			setValue(
				`preform_operations.${index}.date`,
				cancelOperationInfo.closingDate ? cancelOperationInfo.closingDate : dayjs().toString(),
			);

			// если в отмененной операции были значения - переносим их в следующую неотмененную операцию
			if (
				cancelOperationInfo.operation?.count_in_number ||
				cancelOperationInfo.operation?.count_out_number ||
				cancelOperationInfo.operation?.wasted
			) {
				if (
					nextNotCanceledOperation &&
					nextNotCanceledOperationIndex !== -1 &&
					nextNotCanceledOperationIndex !== undefined
				) {
					!nextNotCanceledOperation.count_in_number
						? setValue(
								`preform_operations.${nextNotCanceledOperationIndex}.count_in_number`,
								cancelOperationInfo.operation?.count_in_number,
						  )
						: null;
					!nextNotCanceledOperation.count_out_number
						? setValue(
								`preform_operations.${nextNotCanceledOperationIndex}.count_out_number`,
								cancelOperationInfo.operation?.count_out_number,
						  )
						: null;
					!nextNotCanceledOperation.wasted
						? setValue(
								`preform_operations.${nextNotCanceledOperationIndex}.wasted`,
								cancelOperationInfo.operation?.wasted,
						  )
						: null;
				}
			}

			// очищаем значения в текущей (отмененной) операции
			setValue(`preform_operations.${index}.count_in_number`, undefined);
			setValue(`preform_operations.${index}.count_out_number`, undefined);
			setValue(`preform_operations.${index}.wasted`, undefined);
		}

		setCancelOperationInfo({
			closingEmployeeFio: '',
			operation: undefined,
			operationType: undefined,
			isModalOpen: false,
			closingDate: '',
		});
	}, [cancelOperationInfo, fields, setValue, setCancelOperationInfo]);

	// возобновление операции
	const handleRenewalOperation = useCallback(() => {
		const index = fields?.findIndex((operation) => operation.id === renewalOperation.operation?.id) ?? -1;

		if (Number(index) > -1) {
			setValue(`preform_operations.${index}.is_canceled`, false);
			setValue(`preform_operations.${index}.fio`, '');
		}

		setRenewalOperation({
			operation: undefined,
			operationType: undefined,
			isModalOpen: false,
		});
	}, [renewalOperation.operation?.id, fields, setValue, setRenewalOperation]);

	const sortedOperationsTypes = useMemo(() => (fields ? [...fields].sort(byStepId) : []), [fields]);
	const emptyRows = useCallback((length: number) => {
		const rows = new Array(length).fill({});
		return rows.map((_, idx) => (
			<Tr key={idx}>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
			</Tr>
		));
	}, []);

	const customIndex = Number(preformMap?.preform_operations?.length);
	const customOperations = [{}, {}] as FormPreformModel['preform_operations'];

	const renderOperationRow = (field: PreformOperationResponse, index: number) => {
		const fieldEditable = !field.is_canceled;

		return (
			<Tr key={field.id ?? index}>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''} style={{ textAlign: 'left' }}>
					{fieldEditable ? (
						<Controller
							name={`preform_operations.${index}.step_id`}
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<Input
									backgroundColor="transparent"
									tabIndex={7}
									isInvalid={!!error}
									onChange={onChange}
									value={value}
									name={`preform_operations.${index}.step_id`}
								/>
							)}
						/>
					) : (
						<Text p="6px">{field.step_id}</Text>
					)}
				</Td>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''} style={{ textAlign: 'left' }}>
					<chakra.div w="100%" h="100%" display="flex" justifyContent="space-between" alignItems="center">
						{fieldEditable ? (
							<Controller
								name={`preform_operations.${index}.name`}
								control={control}
								render={({ field: fieldData, fieldState: { error } }) => {
									return <Input tabIndex={7} isInvalid={!!error} {...fieldData} h="100%" flex="1" />;
								}}
							/>
						) : (
							<Text textAlign="left" padding={'8px 1px 8px 6px'} fontWeight="normal">
								{field.name}
							</Text>
						)}
						<Controller
							name={`preform_operations.${index}.is_canceled`}
							control={control}
							render={() => {
								if (field.is_canceled) {
									return canRenew ? <>{renderRenewOperationButton(field, 'operation')}</> : <></>;
								}
								if (!field.date || !field.fio) {
									return <>{renderCancelButton(field, 'operation')}</>;
								}
								return <></>;
							}}
						/>
					</chakra.div>
				</Td>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''} style={{ textAlign: 'left' }}>
					{fieldEditable ? (
						<Controller
							name={`preform_operations.${index}.date`}
							control={control}
							render={({ field: { onChange }, fieldState: { error } }) => (
								<DatePicker
									style={{ width: '100%', height: '100%' }}
									tabIndex={7}
									status={error ? 'error' : ''}
									onChange={onChange}
									format={DEFAULT_YEAR_FORMAT}
									defaultValue={
										field.date && dayjs(field.date).isValid() ? dayjs(field.date) : undefined
									}
									name={`preform_operations.${index}.date`}
								/>
							)}
						/>
					) : (
						<Text fontWeight="normal" textAlign="left">
							{field.date ? dayjs(field.date).format(DEFAULT_YEAR_FORMAT) : ''}
						</Text>
					)}
				</Td>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''} style={{ textAlign: 'left' }}>
					{fieldEditable && (!isOperationDone(field) || userRole === UserRoles.admin) ? (
						<Controller
							name={`preform_operations.${index}.count_in_number`}
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<Input
									backgroundColor="transparent"
									tabIndex={7}
									onChange={(e) => {
										onChange(
											Number.isNaN(e.target.value) || !e.target.value ? null : e.target.value,
										);
									}}
									isInvalid={!!error}
									value={convertNumberToNumberStringWithComma(
										String(value).replace(/[^0-9,.хx]/g, ''),
									)}
									name={`preform_operations.${index}.count_in_number`}
								/>
							)}
						/>
					) : (
						<Text
							p="6px"
							fontWeight="normal"
							textAlign={!isOperationDoneAndEmptyField(field, field.count_in_number) ? 'left' : 'center'}
						>
							{!isOperationDoneAndEmptyField(field, field.count_in_number) ? field.count_in_number : '-'}
						</Text>
					)}
				</Td>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''} style={{ textAlign: 'left' }}>
					{fieldEditable ? (
						<Controller
							name={`preform_operations.${index}.fio`}
							control={control}
							render={({ field: { value, onChange } }) => (
								<Select
									tabIndex={7}
									filterOption={(input, option) =>
										(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
									}
									allowClear
									showSearch
									placeholder="Выберите ФИО"
									optionFilterProp="children"
									value={value}
									onChange={(e) => {
										onChange(e);
										setValue(
											`preform_operations.${index}.fio`,
											e ? employees.find((el) => el.value === e)?.label : '',
										);
									}}
									options={employees}
									style={{ width: '100%' }}
									className={classNames(styles['custom-select'])}
								/>
							)}
						/>
					) : (
						<Text p="6px" fontWeight="normal" textAlign="center">
							{field.fio}
						</Text>
					)}
				</Td>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
					{fieldEditable && (!isOperationDone(field) || userRole === UserRoles.admin) ? (
						<Controller
							name={`preform_operations.${index}.count_out_number`}
							control={control}
							render={({
								field: { onChange, value },
								fieldState: { error },
								formState: { defaultValues },
							}) => (
								<Input
									backgroundColor="transparent"
									tabIndex={7}
									onChange={(e) => {
										const inputValue =
											Number.isNaN(e.target.value) || !e.target.value ? null : e.target.value;
										onChange(inputValue);
										if (index >= customIndex) {
											return;
										}

										if (
											!defaultValues?.preform_operations?.[index + 1]?.count_in_number &&
											index !== customIndex - 1
										) {
											// если это не последняя операция, расчет годных (штук) в этой операции переходит в грамм, штук следующей НЕ ОТМЕНЕННОЙ операции
											setValue(
												`preform_operations.${index + 1}.count_in_number`,
												convertNumberToNumberStringWithComma(inputValue),
											);
										}
									}}
									isInvalid={!!error}
									value={convertNumberToNumberStringWithComma(
										String(value).replace(/[^0-9,.хx]/g, ''),
									)}
									name={`preform_operations.${index}.count_out_number`}
								/>
							)}
						/>
					) : (
						<Text
							p="6px"
							fontWeight="normal"
							textAlign={!isOperationDoneAndEmptyField(field, field.count_out_number) ? 'left' : 'center'}
						>
							{!isOperationDoneAndEmptyField(field, field.count_out_number)
								? field.count_out_number
								: '-'}
						</Text>
					)}
				</Td>
				<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
					{fieldEditable ? (
						<Controller
							name={`preform_operations.${index}.wasted`}
							control={control}
							render={({ field: { onChange, value, name }, fieldState: { error } }) => (
								<Input
									tabIndex={7}
									isInvalid={!!error}
									onChange={onChange}
									// @ts-expect-error TODO: fix after swagger update
									value={
										value && (Number.isNaN(Number(value)) || Number(value) > 0)
											? convertNumberToNumberStringWithComma(
													String(value).replace(/[^0-9,.хx]/g, ''),
											  )
											: null
									}
									name={name}
									className={field.is_canceled ? styles['field-disabled'] : ''}
								/>
							)}
						/>
					) : (
						<Text p="6px" fontWeight="normal" textAlign="left">
							{!field.wasted || Number(field.wasted) === 0 ? '' : field.wasted}
						</Text>
					)}
				</Td>{' '}
				<Td></Td>
				<Td></Td>
			</Tr>
		);
	};

	return (
		<>
			{cancelOperationInfo.isModalOpen && (
				<ModalOperation
					data={cancelOperationInfo}
					onSubmit={handleCancelOperation}
					variant="cancel"
					employee={employees}
					setData={setCancelOperationInfo}
				/>
			)}
			{renewalOperation.isModalOpen && (
				<ModalOperation
					data={renewalOperation}
					onSubmit={handleRenewalOperation}
					variant="renewal"
					setData={setRenewalOperation}
				/>
			)}
			<Flex className={defaultStyles['operations']}>
				<Table>
					<Thead>
						<Tr>
							<Th colSpan={2}>Операции</Th>
							<Th colSpan={5}>Результаты работы</Th>
							<Th colSpan={2}>Подписи</Th>
						</Tr>
						<Tr>
							<Th w="60px" style={{ textAlign: 'left' }}>
								№
							</Th>
							<Th w="400px">Наименование</Th>
							<Th w="140px">Дата</Th>
							<Th maxW="200px">Выдано в работу м X шт.</Th>
							<Th maxW="225px">ФИО исполнителя</Th>
							<Th w="150px">Годных м X шт</Th>
							<Th w="120px">Брак м X шт</Th>
							<Th w="120px">Исполнитель</Th>
							<Th w="120px">Кладовщик</Th>
						</Tr>
					</Thead>
					<Tbody>
						{!isEditable && !isCreationMode && (
							<>
								{preformMap?.preform_operations?.map((operation) => {
									return (
										<Tr
											key={operation.id}
											className={operation.is_canceled ? styles['field-disabled'] : ''}
										>
											<Td style={{ textAlign: 'left' }}>
												{operation.step_id < 10 ? '0' + operation.step_id : operation.step_id}
											</Td>
											<Td style={{ textAlign: 'left', padding: '0px 2px 0px 8px' }}>
												<Text>{operation.name}</Text>
											</Td>
											<Td>
												{dayjs(operation.date).isValid()
													? dayjs(operation.date).format(DEFAULT_YEAR_FORMAT)
													: ''}
											</Td>
											<Td>
												{!isOperationDoneAndEmptyField(operation, operation.count_in_number)
													? operation.count_in_number
													: '-'}
											</Td>
											<Td>
												{!isOperationDoneAndEmptyField(operation, operation.fio)
													? operation.fio
													: '-'}
											</Td>
											<Td>
												{!isOperationDoneAndEmptyField(operation, operation.count_out_number)
													? operation.count_out_number
													: '-'}
											</Td>
											<Td>
												{!operation.wasted || Number(operation.wasted) === 0
													? ''
													: operation.wasted}
											</Td>
											<Td></Td>
											<Td></Td>
										</Tr>
									);
								})}
								{emptyRows(2)}
							</>
						)}
						{!isEditable && isCreationMode && sortedOperationsTypes?.length ? (
							<>
								{sortedOperationsTypes?.map((operation) => (
									<Tr
										key={operation.id}
										className={operation.is_canceled ? styles['field-disabled'] : ''}
									>
										<Td style={{ textAlign: 'left' }}>
											{operation.step_id < 10 ? '0' + operation.step_id : operation.step_id}
										</Td>
										<Td style={{ textAlign: 'left' }}>{operation.name}</Td>
										<Td></Td>
										<Td></Td>
										<Td></Td>
										<Td></Td>
										<Td></Td>
										<Td></Td>
										<Td></Td>
									</Tr>
								))}
								{emptyRows(2)}
							</>
						) : (
							<></>
						)}
						{isEditable && !isCreationMode && (
							<>
								{sortedOperationsTypes?.map((field, index) => renderOperationRow(field, index))}
								{/* Пустые строки в таблице */}
								{customOperations?.map((field, index) => {
									return renderOperationRow(field, index + customIndex);
								})}
							</>
						)}
					</Tbody>
				</Table>
			</Flex>
		</>
	);
};
